import { useMemo, useState } from "react";
import { UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { ROLES } from "@/lib/constants";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { RepRow } from "@/components/crm/RepRow";

const AddRepForm = () => {
  const { upsertRep } = useData();
  const [name, setName] = useState("");
  const [role, setRole] = useState("Setter");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("Name is required");
    try {
      const { data } = await api.post("/reps", { name: name.trim(), role });
      upsertRep(data);
      setName("");
      setError("");
      toast.success(`${data.name} added as ${data.role}`);
    } catch (err) {
      setError(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} data-testid="team-add-form" className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex-1">
          <input data-testid="team-add-name-input" value={name} placeholder="Rep name" onChange={(e) => { setName(e.target.value); setError(""); }} className={`inp ${error ? "inp-invalid" : ""}`} />
          {error && <p data-testid="team-add-error" className="field-error mt-1">{error}</p>}
        </div>
        <select data-testid="team-add-role-select" value={role} onChange={(e) => setRole(e.target.value)} className="inp sm:w-36">
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="submit" data-testid="team-add-submit-btn" className="btn-primary"><UserPlus className="h-4 w-4" /> Add rep</button>
      </div>
    </form>
  );
};

export default function TeamPage() {
  const { reps, leads } = useData();
  const counts = useMemo(() => {
    const c = {};
    leads.forEach((l) => [l.setter_id, l.closer_id].forEach((id) => { if (id) c[id] = (c[id] || 0) + 1; }));
    return c;
  }, [leads]);
  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="team-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Team</h1>
        <p className="text-sm text-slate-400">Setters and closers appear in the dropdowns on lead cards. Deactivated reps stay on old cards.</p>
      </div>
      <AddRepForm />
      {reps.length === 0 ? (
        <div data-testid="team-empty" className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-700 bg-slate-900 py-12 text-slate-400">
          <Users className="h-6 w-6" /> No reps yet. Add your first setter or closer.
        </div>
      ) : (
        <ul data-testid="team-list" className="divide-y divide-slate-800 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
          {reps.map((r) => <RepRow key={`${r.id}-${r.name}-${r.role}`} rep={r} leadCount={counts[r.id] || 0} />)}
        </ul>
      )}
    </div>
  );
}
